import { Link, type LinkProps } from '@tanstack/react-router';
import { useEffect, useRef, type ReactNode } from 'react';

import './barraDeAbas.css';

export type Aba = {
  /** Destino da aba, no vocabulário de rotas do roteador. */
  para: NonNullable<LinkProps['to']>;
  rotulo: string;
  /** Decorativo: quem nomeia a aba é o `rotulo`. */
  icone: ReactNode;
};

export type PropsBarraDeAbas = {
  abas: ReadonlyArray<Aba>;
};

/** Barra de abas no padrão do iOS, presa ao rodapé.
 *
 *  Os rótulos se recolhem quando não cabem — inteiros ou nenhum, nunca
 *  cortados no meio da palavra — e continuam no nome acessível do link. O
 *  corte é uma _container query_ em `em` (ver `barraDeAbas.css`).
 *
 *  A barra publica a própria altura em `--altura-abas`: quem se apoia nela (o
 *  `Toast` e o respiro do conteúdo) precisa da altura real, que muda com o
 *  tamanho de texto do usuário, não de um `4.5rem` chutado. */
export function BarraDeAbas({ abas }: PropsBarraDeAbas) {
  const barra = useRef<HTMLElement>(null);
  useEffect(() => {
    const elemento = barra.current;
    if (!elemento || typeof ResizeObserver === 'undefined') return;
    const medir = () => {
      document.documentElement.style.setProperty(
        '--altura-abas',
        `${String(Math.round(elemento.getBoundingClientRect().height))}px`,
      );
    };
    medir();
    const observador = new ResizeObserver(medir);
    observador.observe(elemento);
    return () => {
      observador.disconnect();
      // A propriedade é global (`documentElement`): deixá-la para trás depois
      // do desmonte seria lixo de estado, e o padrão do token voltaria a valer
      // para quem se apoia na altura.
      document.documentElement.style.removeProperty('--altura-abas');
    };
  }, []);
  return (
    <nav className="abas" aria-label="Principal" ref={barra}>
      {abas.map(({ para, rotulo, icone }) => (
        <Link
          key={String(para)}
          to={para}
          className="abas__aba"
          activeProps={{ 'aria-current': 'page' }}
        >
          {icone}
          <span className="abas__rotulo">{rotulo}</span>
        </Link>
      ))}
    </nav>
  );
}
